import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, CheckCircle2, Package, Truck, Home as HomeIcon, MapPin } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const STEP_ICONS = { paid: CheckCircle2, processing: Package, shipped: Truck, delivered: HomeIcon };

export default function Track() {
  const [params] = useSearchParams();
  const { format } = useCurrency();
  const [orderId, setOrderId] = useState(params.get("order") || "");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const doTrack = async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/orders/track?order=${encodeURIComponent(id)}`);
      setOrder(data);
    } catch (err) {
      setOrder(false);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Order not found");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (params.get("order")) doTrack(params.get("order")); }, []);

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
      <div className="mb-2 font-mono text-xs uppercase tracking-widest text-[#00F0FF]">Order Tracking</div>
      <h1 className="font-display text-3xl font-bold text-white">Track your order</h1>
      <p className="mt-2 text-slate-400">Enter your order ID (e.g. DW-XXXXXXXX) to see real-time status.</p>

      <form onSubmit={(e) => { e.preventDefault(); doTrack(orderId); }} className="mt-6 flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <Input value={orderId} onChange={(e) => setOrderId(e.target.value)} placeholder="DW-XXXXXXXX" className="rounded-xl border-white/10 bg-white/5 pl-10 font-mono" data-testid="track-input" />
        </div>
        <Button type="submit" disabled={loading} className="rounded-xl bg-[#00F0FF] px-6 font-bold text-[#08090E] hover:bg-[#33F3FF]" data-testid="track-submit">{loading ? "…" : "Track"}</Button>
      </form>

      {order === false && <div className="dw-card mt-8 rounded-2xl p-10 text-center text-slate-400" data-testid="track-not-found">No order found with that ID.</div>}

      {order && (
        <div className="mt-8 space-y-6" data-testid="track-result">
          <div className="dw-card rounded-2xl p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div><p className="font-mono text-sm text-[#00F0FF]">#{order.order_id}</p><p className="text-xs text-slate-500">Placed {new Date(order.created_at).toLocaleDateString()}</p></div>
              <span className="rounded-full bg-[#00F0FF]/10 px-3 py-1 font-mono text-xs uppercase tracking-wider text-[#00F0FF]">{order.status}</span>
            </div>
            {order.tracking_number && (
              <div className="mt-4 flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 p-3 text-sm text-slate-300">
                <MapPin className="h-4 w-4 text-[#8A2BE2]" /> {order.carrier} · Tracking <span className="font-mono text-slate-100">{order.tracking_number}</span>
              </div>
            )}
          </div>

          <div className="dw-card rounded-2xl p-6">
            <div className="space-y-6">
              {order.timeline?.map((s, i) => {
                const Icon = STEP_ICONS[s.status] || Package;
                const active = s.done;
                return (
                  <div key={i} className="flex items-start gap-4">
                    <div className="flex flex-col items-center">
                      <div className={`flex h-10 w-10 items-center justify-center rounded-full ${active ? "bg-[#00F0FF]/15 text-[#00F0FF]" : "bg-white/5 text-slate-600"}`}><Icon className="h-5 w-5" /></div>
                      {i < order.timeline.length - 1 && <div className={`mt-1 h-8 w-0.5 ${active ? "bg-[#00F0FF]/40" : "bg-white/10"}`} />}
                    </div>
                    <div className="pt-1"><p className={`text-sm font-semibold ${active ? "text-slate-100" : "text-slate-500"}`}>{s.label}</p>{s.at && <p className="font-mono text-xs text-slate-500">{new Date(s.at).toLocaleString()}</p>}</div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="dw-card rounded-2xl p-6">
            <h3 className="font-display text-sm font-semibold text-white">Items · Total {format(order.total_usd)}</h3>
            <div className="mt-3 space-y-2">
              {order.items?.map((i) => <div key={i.product_id} className="flex justify-between text-sm text-slate-300"><span>{i.name} × {i.quantity}</span><span className="font-mono">{format(i.price_usd * i.quantity)}</span></div>)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
